/**
 * Layout "Conexões": Fruchterman–Reingold no cliente, para o grafo que o backend devolve sem
 * coordenadas (rota `rede/grafo` sem `layout`).
 *
 * Determinístico de propósito — posição inicial num círculo pela ordem dos nós e número fixo de
 * iterações, sem `Math.random`: o mesmo grafo sempre sai igual (e dá para testar). A raiz fica
 * presa em (0,0). Com o teto de 120 nós do backend, 300 iterações cabem folgadas num quadro.
 */
export function layoutDeForca<T extends { id: string; raiz: boolean }>(
  nos: T[],
  arestas: { origem: string; destino: string }[],
  iteracoes = 300
): (T & { x: number; y: number })[] {
  const total = nos.length;
  if (total === 0) return [];

  const largura = Math.max(400, Math.sqrt(total) * 140);
  const k = Math.sqrt((largura * largura) / total);
  const raio = largura / 3;

  const pos = nos.map((no, i) => {
    if (no.raiz) return { x: 0, y: 0 };
    const angulo = (2 * Math.PI * i) / total;
    return { x: raio * Math.cos(angulo), y: raio * Math.sin(angulo) };
  });

  const indice = new Map(nos.map((no, i) => [no.id, i]));
  const pares = arestas
    .map(a => [indice.get(a.origem), indice.get(a.destino)] as const)
    .filter(
      (p): p is readonly [number, number] =>
        p[0] !== undefined && p[1] !== undefined && p[0] !== p[1]
    );

  const MIN = 0.01;
  for (let it = 0; it < iteracoes; it++) {
    const temperatura = (largura / 10) * (1 - it / iteracoes);
    const dx = new Float64Array(total);
    const dy = new Float64Array(total);

    for (let i = 0; i < total; i++) {
      for (let j = i + 1; j < total; j++) {
        let vx = pos[i].x - pos[j].x;
        let vy = pos[i].y - pos[j].y;
        let d = Math.hypot(vx, vy);
        if (d < MIN) {
          // Dois nós no mesmo ponto: separa numa direção fixa derivada dos índices.
          vx = Math.cos(i + j);
          vy = Math.sin(i + j);
          d = MIN;
        }
        const f = (k * k) / d;
        dx[i] += (vx / d) * f;
        dy[i] += (vy / d) * f;
        dx[j] -= (vx / d) * f;
        dy[j] -= (vy / d) * f;
      }
    }

    for (const [a, b] of pares) {
      const vx = pos[a].x - pos[b].x;
      const vy = pos[a].y - pos[b].y;
      const d = Math.max(MIN, Math.hypot(vx, vy));
      const f = (d * d) / k;
      dx[a] -= (vx / d) * f;
      dy[a] -= (vy / d) * f;
      dx[b] += (vx / d) * f;
      dy[b] += (vy / d) * f;
    }

    for (let i = 0; i < total; i++) {
      if (nos[i].raiz) continue;
      const d = Math.max(MIN, Math.hypot(dx[i], dy[i]));
      const passo = Math.min(d, temperatura);
      pos[i] = { x: pos[i].x + (dx[i] / d) * passo, y: pos[i].y + (dy[i] / d) * passo };
    }
  }

  return nos.map((no, i) => ({ ...no, x: pos[i].x, y: pos[i].y }));
}
